import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MapPin, Sparkles, Compass, Twitter, Linkedin, Github, Instagram, Youtube, Hash, Star, Loader2, Globe } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_ICONS = {
  twitter: Twitter, linkedin: Linkedin, github: Github,
  instagram: Instagram, youtube: Youtube, roblox: Compass,
  discord: Hash, tiktok: Star,
};

const BADGE_COLORS = {
  Executive:   'bg-amber-500/15 text-amber-300 border-amber-500/30',
  Staff:       'bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/30',
  Moderator:   'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  VIP:         'bg-purple-500/15 text-purple-300 border-purple-500/30',
  Verified:    'bg-blue-500/15 text-blue-300 border-blue-500/30',
  default:     'bg-white/[0.06] text-white/60 border-white/10',
};

const gradientFor = (str = '') => {
  const grads = [
    'from-[#00E5FF] to-[#8A2BE2]', 'from-fuchsia-500 to-rose-500',
    'from-amber-400 to-orange-500', 'from-emerald-400 to-cyan-500',
    'from-indigo-500 to-purple-500', 'from-pink-500 to-red-500',
    'from-sky-400 to-blue-600', 'from-yellow-400 to-amber-600',
  ];
  let sum = 0; for (let i = 0; i < str.length; i++) sum += str.charCodeAt(i);
  return grads[sum % grads.length];
};

// ephemeral cache — profiles rarely change during a session
const profileCache = new Map();

const UserHoverCard = ({ username, children, placement = 'top' }) => {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const cardRef = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);

  const load = async () => {
    if (!username) return;
    if (profileCache.has(username)) { setProfile(profileCache.get(username)); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/public-profile/${encodeURIComponent(username)}`);
      profileCache.set(username, r.data);
      setProfile(r.data);
    } catch {
      profileCache.set(username, null);
      setProfile(null);
    } finally { setLoading(false); }
  };

  const handleEnter = () => {
    clearTimeout(closeTimer.current);
    openTimer.current = setTimeout(() => { setOpen(true); load(); }, 250);
  };
  const handleLeave = () => {
    clearTimeout(openTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  };

  useEffect(() => () => { clearTimeout(openTimer.current); clearTimeout(closeTimer.current); }, []);

  const initial = (profile?.display_name || username || '?').trim().charAt(0).toUpperCase();

  return (
    <span
      ref={wrapperRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
      className="relative inline-flex"
      data-testid={`hover-trigger-${username}`}
    >
      {children}
      {open && (
        <div
          ref={cardRef}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          data-testid={`hover-card-${username}`}
          className={`absolute z-50 w-72 rounded-2xl border border-white/10 bg-[#0A0A12]/95 backdrop-blur-2xl shadow-[0_20px_60px_-10px_rgba(0,0,0,0.9),0_0_40px_-10px_rgba(0,229,255,0.15)] p-4 ${
            placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
          } left-0 sm:left-1/2 sm:-translate-x-1/2`}
          style={{ animation: 'hoverFade 0.15s ease-out' }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-6 text-white/40">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : !profile ? (
            <div className="text-center py-4">
              <p className="text-white/70 text-sm">@{username}</p>
              <p className="text-white/30 text-xs mt-1">Profile unavailable</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-start gap-3 mb-3">
                {profile.avatar_url ? (
                  <img src={profile.avatar_url.startsWith('http') ? profile.avatar_url : `${BACKEND_URL}${profile.avatar_url}`}
                    alt={profile.display_name}
                    className="w-14 h-14 rounded-2xl object-cover border border-white/10" />
                ) : (
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradientFor(profile.username)} flex items-center justify-center text-white font-bold text-xl shadow-lg`}>
                    {initial}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-white font-semibold text-sm truncate">{profile.display_name}</p>
                    {profile.pronouns && (
                      <span className="px-1.5 py-0.5 rounded-md bg-white/[0.06] border border-white/10 text-white/50 text-[9px]">
                        {profile.pronouns}
                      </span>
                    )}
                  </div>
                  <p className="text-white/40 text-xs">@{profile.username}</p>
                  {profile.location && (
                    <p className="text-white/50 text-[11px] mt-1 inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {profile.location}
                    </p>
                  )}
                </div>
              </div>

              {/* Badges */}
              {profile.badges?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {profile.badges.slice(0, 4).map(b => {
                    const cls = BADGE_COLORS[b] || BADGE_COLORS.default;
                    return (
                      <span key={b} className={`px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider border ${cls}`}>
                        {b}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Bio */}
              {profile.bio && (
                <p className="text-white/60 text-xs leading-relaxed mb-3 line-clamp-3 italic">
                  "{profile.bio}"
                </p>
              )}

              {/* Interests */}
              {profile.interests?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {profile.interests.slice(0, 5).map(t => (
                    <span key={t} className="px-2 py-0.5 rounded-full bg-gradient-to-r from-[#8A2BE2]/15 to-[#00E5FF]/10 border border-white/10 text-white/60 text-[10px]">
                      {t}
                    </span>
                  ))}
                </div>
              )}

              {/* Socials */}
              {profile.socials && Object.keys(profile.socials).filter(k => profile.socials[k]).length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-3 border-t border-white/[0.06]">
                  {Object.entries(profile.socials).filter(([_, v]) => v).slice(0, 6).map(([k, v]) => {
                    const Icon = SOCIAL_ICONS[k] || Globe;
                    return (
                      <div key={k} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.08] text-white/50 text-[10px]">
                        <Icon className="w-2.5 h-2.5" />
                        <span className="truncate max-w-[80px]">{v}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
          <style>{`@keyframes hoverFade { from { opacity: 0; transform: translateY(-4px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }`}</style>
        </div>
      )}
    </span>
  );
};

export default UserHoverCard;
